import React, { useEffect } from 'react'
import type { GetServerSideProps, NextPage } from 'next'
import Head from 'next/head'
import Cookies from 'universal-cookie'
import {
  ColorPalette,
  Alternate,
  Style,
  Error,
  ErrorProps,
  Environment,
  AppContextProps,
  AppContextProvider,
} from '@wap-client/core'
import {
  configService,
  pageService,
  siteService,
  Page as IPage,
  queryService,
} from '@wap-client/services'
import { NodeEnv } from '@wap-client/constants/enums'

import SeoMeta from '@/core/components/seo-meta'
import render from '@/core/render'
import { Location } from '@/core/components/location/types'
import { goTurkiyeClient } from '@/services/client'
import { isEmpty } from 'lodash'
import langs from 'utils/langs.json'
import momentLanguageImport from '@/utils/functions/momentLanguageImport'

type PageProps = AppContextProps & {
  error?: ErrorProps
}

const Page: NextPage<PageProps> = ({ error, ...context }) => {
  momentLanguageImport(context?.language)

  useEffect(() => {
    const cookie = new Cookies()

    if (process.env.NEXT_PUBLIC_ENV === NodeEnv.Development) {
      console.log(context)
    }

    if (context.language) {
      const host =
        process.env.NEXT_PUBLIC_ENV === NodeEnv.Production
          ? `.${window.location.host}`
          : ``
      cookie.set('language', context.language, {
        domain: host,
        path: '/',
      })
    }
  }, [context])

  if (error) {
    return <Error {...error} />
  }

  return (
    <React.StrictMode>
      <SeoMeta context={context} />
      <Head>
        <Alternate context={context} />
      </Head>
      <ColorPalette context={context} />
      <AppContextProvider value={context}>
        {render(context.page.layout, context.page.views)}
      </AppContextProvider>
      <Style context={context} />
    </React.StrictMode>
  )
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const cookieLanguage = context.req.cookies?.language
  const props: Partial<PageProps> = {
    container: 'browser',
    headers: context.req.headers,
    cookies: context.req.cookies,
    params: context.query,
    language: cookieLanguage,
  }

  if (context.req.headers.host) {
    const environment = new Environment()
    const host = context.req.headers.host.replace('www.', '')
    const params = context.query.pathname || []
    let pathname = typeof params === 'string' ? params : params.join('/')
    let parts = pathname.split('/')

    const isProduction = process.env.NEXT_PUBLIC_ENV === NodeEnv.Production
    const langIndex = isProduction ? 0 : 1

    const langMap: Record<string, string> = langs

    if (langMap[parts[langIndex]]) {
      parts[langIndex] = langMap[parts[langIndex]]
    }

    pathname = parts.join('/')

    if (process.env.NEXT_PUBLIC_ENV === NodeEnv.Production) {
      try {
        const site = await siteService.get(environment.context, host)

        environment.update({
          cdn: process.env.NEXT_PUBLIC_CDN || site.cdn,
          environment: process.env.NEXT_PUBLIC_ENV,
          key: site.key,
          service: `${site.service}/${site.key}`,
          url: site.url,
        })

        if (parts.length) {
          props.language = parts[0] || 'en-us'
        }
      } catch (error: any) {
        return { props: { error } }
      }
    } else {
      if (parts.length) {
        environment.set('key', parts[0])
        environment.set('environment', NodeEnv.Development)

        if (parts[1]) {
          pathname = parts.slice(1, parts.length).join('/')

          props.language = parts[1]
        } else {
          pathname = ''
          props.language = 'en-us'
        }
      }
    }

    try {
      const config = await configService.get(environment.context, {
        language: String(props.language),
      })

      if (
        pathname.split('/').length === 1 &&
        config.languages.find((lang) => lang.code === pathname)
      ) {
        pathname = config.settings.homepage
      }

      props.settings = config.settings
      props.themes = config.themes
      props.languages = config.languages
      props.language = config.language
      props.environment = environment.context
    } catch (error: any) {
      props.error = error
    }

    if (props.settings && props.themes) {
      try {
        let page: IPage | null = null
        const previewId = context.query['previewId']
        const abtest = context.req.cookies?.wis_s_tasarimab
        const abPathname = abtest ? `${pathname}-${abtest}` : pathname

        if (previewId) {
          page = await pageService.preview(
            environment.context,
            String(previewId),
          )
        } else {
          page = await pageService.get(environment.context, {
            path: pathname || props.settings.homepage,
          })
        }

        if (page) {
          props.page = page
        }

        const location = await queryService
          .run<Location[]>(environment.context, {
            name: 'get-app-eventgoogle',
            language: page.language,
          })
          .catch((error) => {
            return null
          })

        if (location && location?.data.length > 0) {
          props.tenant = {
            location: location.data,
          }
        }

        if (props?.page?.fields?.SustainabilityKategori?.id) {
          const category = await queryService
            .run<Location[]>(environment.context, {
              name: 'get-app-sustainability',
              language: page.language,
              query: `id=${props?.page?.fields?.SustainabilityKategori?.id}`,
            })
            .catch((error) => {
              return null
            })

          if (category && category?.data.length > 0) {
            props.tenant = {
              category: category.data?.[0],
            }
          }
        }

        props.theme = props.themes.find((theme) => {
          if (page && page.theme) {
            return theme.id === page.theme
          } else {
            return theme.isDefault === true
          }
        })

        /** Custom my routes endpoint */
        if (props.settings.routes.route) {
          const myRouteId = context.query.key
          const myRoutePath = props.settings.routes.route.href

          if (page && page.path === myRoutePath) {
            if (!isEmpty(myRouteId)) {
              const url = `/${environment.context.key}/myroutes/${props.language}/${myRouteId}`
              const response = await goTurkiyeClient.get<any>(url)
              const { data } = response.data

              if (data) {
                props.tenant = {
                  myRoute: data,
                }

                return { props }
              }
            }

            return {
              redirect: {
                permanent: true,
                destination: props.settings.homepage,
              },
            }
          }
        }
      } catch (error: any) {
        props.error = error
      }
    }
  }

  return { props }
}

export default Page
